import {FunctionComponent} from 'react';
import {FormikProps} from 'formik';
import {useTranslation} from 'react-i18next';
import {AdvisorProfileFormValues} from '@/components/advisor-profile-form/interfaces/form-values';
import FieldWrapper from '@/components/form/field-wrapper';
import FormControl from '@/components/form/form-control';
import Input from '@/components/form/input';
import MaskedInput from '@/components/form/masked-input';
import {FieldMetadata} from '@/interfaces/form';
import {CombinedFields} from './combined-fields';

interface AdvisorCodesSecondSectionProps {
  fieldsMetadata: {
    altAdvisorCode3: FieldMetadata;
    altAdvisorCode4: FieldMetadata;
  };
  getFieldMessage: (fieldId: string) => string | undefined;
  formik: Pick<FormikProps<Partial<AdvisorProfileFormValues>>, 'handleChange' | 'handleBlur' | 'values' | 'errors'>;
}

export const AdvisorCodesSecondSection: FunctionComponent<AdvisorCodesSecondSectionProps> = ({
  fieldsMetadata,
  getFieldMessage,
  formik: {handleChange, handleBlur, values, errors},
}) => {
  const {t} = useTranslation('common', {keyPrefix: 'components.advisor-profile-form'});

  return (
    <div className="grid grid-cols-1 items-start gap-4 md:grid-cols-2">
      <CombinedFields
        labelFor="altAdvisorCode3"
        data-testid="CONS9_14-label"
        label={t('label-advisor-alt-code', {index: 3})}
      >
        <FormControl>
          <FieldWrapper identifier="CONS9_14" hidden={fieldsMetadata.altAdvisorCode3.isHidden}>
            <Input
              data-testid="CONS9_14-input"
              id="CONS9_14"
              name="CONS9_14"
              readOnly
              value={values.cons9_generalAgentCode ?? ''}
            />
          </FieldWrapper>
        </FormControl>

        <FormControl
          isRequired={fieldsMetadata.altAdvisorCode3.isRequired}
          isInError={!!errors.altAdvisorCode3}
          errors={[errors.altAdvisorCode3 ?? '']}
          warningMessage={getFieldMessage('cons14_altAdvisorCode3')}
        >
          <FieldWrapper identifier="altAdvisorCode3" hidden={fieldsMetadata.altAdvisorCode3.isHidden}>
            <MaskedInput
              data-testid="altAdvisorCode3_input"
              id="altAdvisorCode3"
              mask="&&&&&&"
              isUppercasedMask={true}
              maxLength={6}
              name="altAdvisorCode3"
              onBlur={handleBlur('altAdvisorCode3')}
              onAccept={handleChange('altAdvisorCode3')}
              readOnly={fieldsMetadata.altAdvisorCode3.isReadOnly}
              value={values.altAdvisorCode3 || ''}
            />
          </FieldWrapper>
        </FormControl>
      </CombinedFields>

      <CombinedFields
        labelFor="altAdvisorCode4"
        data-testid="CONS9_15-label"
        label={t('label-advisor-alt-code', {index: 4})}
      >
        <FormControl>
          <FieldWrapper identifier="CONS9_15" hidden={fieldsMetadata.altAdvisorCode4.isHidden}>
            <Input
              data-testid="CONS9_15-input"
              id="CONS9_15"
              name="CONS9_15"
              readOnly
              value={values.cons9_generalAgentCode ?? ''}
            />
          </FieldWrapper>
        </FormControl>

        <FormControl
          isRequired={fieldsMetadata.altAdvisorCode4.isRequired}
          isInError={!!errors.altAdvisorCode4}
          errors={[errors.altAdvisorCode4 ?? '']}
          warningMessage={getFieldMessage('cons15_altAdvisorCode4')}
        >
          <FieldWrapper identifier="altAdvisorCode4" hidden={fieldsMetadata.altAdvisorCode4.isHidden}>
            <MaskedInput
              data-testid="altAdvisorCode4_input"
              id="altAdvisorCode4"
              mask="&&&&&&"
              isUppercasedMask={true}
              maxLength={6}
              name="altAdvisorCode4"
              onBlur={handleBlur('altAdvisorCode4')}
              onAccept={handleChange('altAdvisorCode4')}
              readOnly={fieldsMetadata.altAdvisorCode4.isReadOnly}
              value={values.altAdvisorCode4 || ''}
            />
          </FieldWrapper>
        </FormControl>
      </CombinedFields>
    </div>
  );
};
